import ActiveIcon from "@/components/icons/ActiveIcon";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";
import { periodeIsActive, stringDateToPP } from "@/util/date";
import { orderBy } from "@/util/orderBy";
import { formatLessons } from "@/util/number";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import {
  mapIndberetningStatusBadgeVariant,
  mapIndberetningStatusLabel,
} from "./paabudUtils";
import { StatusBadge } from "./StatusBadge";
import IngenRegistreringer from "../IngenRegistreringer";

interface PeriodsTableProps {
  periods: DecoratedPersonEfteruddannelse[];
  isLoading?: boolean;
  onSelectPeriod: (period: DecoratedPersonEfteruddannelse) => void;
}

export const PeriodsTable = ({
  periods,
  isLoading,
  onSelectPeriod,
}: PeriodsTableProps) => {
  if (isLoading) {
    return <p className="text-muted">Henter perioder…</p>;
  }

  if (!periods.length) {
    return <IngenRegistreringer text="Ingen efteruddannelsesperioder" />;
  }

  const sortedPeriods = orderBy(
    periods,
    ["periode.tilDatoInklusiv", "periode.fraDato"],
    ["desc", "desc"],
  );

  const handleSelect = (period: DecoratedPersonEfteruddannelse) => {
    onSelectPeriod(period);
  };

  const handleRowKeyDown = (
    event: React.KeyboardEvent,
    period: DecoratedPersonEfteruddannelse,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleSelect(period);
  };

  return (
    <TableRoot>
      <TableHead>
        <th className="indicator" aria-hidden />
        <th>Startdato</th>
        <th>Slutdato</th>
        <th>Lektioner gennemført</th>
        <th>Overført fra forrige periode</th>
        <th>Status</th>
        <th>Frist</th>
        <th className="text-end">Handling</th>
      </TableHead>
      <TableBody>
        {sortedPeriods.map((period) => {
          const isActive = periodeIsActive(
            new Date(period.periode.fraDato),
            new Date(period.periode.tilDatoInklusiv),
          );

          return (
            <tr
              key={period.id}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              aria-label={`Vis dokumenter for perioden ${stringDateToPP(period.periode.fraDato)} - ${stringDateToPP(period.periode.tilDatoInklusiv)}`}
              onClick={() => handleSelect(period)}
              onKeyDown={(event) => handleRowKeyDown(event, period)}
            >
              <td className={`indicator${isActive ? " has-effect" : ""}`}>
                <ActiveIcon />
              </td>
              <td>{stringDateToPP(period.periode.fraDato)}</td>
              <td>{stringDateToPP(period.periode.tilDatoInklusiv)}</td>
              <td>{formatLessons(period.lektionerGennemfoert)}</td>
              <td>{formatLessons(period.lektionerOverfoert)}</td>
              <td>
                {period.indberetningStatusId || period.indberetningStatus ? (
                  <StatusBadge
                    status={
                      period.indberetningStatusId
                        ? mapIndberetningStatusLabel(
                            period.indberetningStatusId,
                          )
                        : period.indberetningStatus!
                    }
                    variant={
                      period.indberetningStatusId
                        ? mapIndberetningStatusBadgeVariant(
                            period.indberetningStatusId,
                          )
                        : "muted"
                    }
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>
                {period.indberetningFrist
                  ? stringDateToPP(period.indberetningFrist)
                  : "-"}
              </td>
              <td className="text-end">
                <button
                  type="button"
                  id={`efteruddannelse-period-vis-${period.id}`}
                  className="btn btn--action btn--sm"
                  title="Vis dokumenter for perioden"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelect(period);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          );
        })}
      </TableBody>
    </TableRoot>
  );
};
